import {
  ContentEditableHtmlEditorPlugin,
  getTextBeforeCursor,
  spliceTextNode,
  TextEditor,
} from '@frontapp/editor-html';

import {FrontWindow} from '../../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {getWindow} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {endsWithLatinCharacter} from '../../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  emojiToShortcode,
  emojiToUnified,
  emojiToUrl,
} from '../../../../../../srcCommon/helpers/emoji/emojiHelpers';
import {EmojiDefinitionAndSkinTone} from '../../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {emojiAllowedAttributes} from '../../../../../../srcCommon/helpers/messageDraftHelpers';
import {
  isInsideOfCodeBlock,
  splitEmojisAndEmoticonsOutsideLinksAndChars,
} from '../../../../../helpers/emoji/emojiAndEmoticonsTextHelpers';
import {EmojiMap} from '../../../../../services/emojis/emojis';

/*
 * Constants.
 */

const sizeMultiplier = 1.4286;
const bottomMarginMultiplier = -0.325;

const initialFontSize = 14;
const initialSize = Math.round(initialFontSize * sizeMultiplier);
const initialBottomMargin = Math.round(initialFontSize * bottomMarginMultiplier * 1e5) / 1e5;

/*
 * Types.
 */

export interface EmojiPluginOptions {
  renderMode: EmojiPluginRenderModesEnum;
  shouldReplaceEmoticons?: boolean;
  emojiValidationMap: EmojiMap;
}

export enum EmojiPluginRenderModesEnum {
  SHORTCODE = 'SHORTCODE',
  UNIFIED = 'UNIFIED',
}

/*
 * Plugin.
 */

export function createEmojiPlugin(options: EmojiPluginOptions): ContentEditableHtmlEditorPlugin {
  return {
    // Allow the older attribute just in case. The newer data-* attribute is allowed by default.
    addAllowedAttributes: emojiAllowedAttributes,
    processText: (element, textNodeAndCursorPositions) => {
      const window = getWindow(element);
      if (!window) {
        return;
      }

      Array.from(textNodeAndCursorPositions).forEach(({node, cursorPosition}) => {
        if (isInsideOfCodeBlock(element, node)) {
          return;
        }

        replaceEmojisInText(options, node, document, window, cursorPosition);
      });
    },
    processTree: (element) => {
      const window = getWindow(element);
      if (!window) {
        return;
      }

      const nodeList = element.querySelectorAll<HTMLImageElement>('img[data-front-emoji-shortcode]');
      for (let i = 0; i < nodeList.length; i++) {
        const node = nodeList[i];
        processEmojiNode(node, window);
      }
    },
    isImageInteractive: (image) => !image.hasAttribute('data-front-emoji-shortcode'),
  };
}

function replaceEmojisInText(
  options: EmojiPluginOptions,
  textNode: Text,
  document: Document,
  window: FrontWindow,
  cursorPosition?: number,
): void {
  let offset = 0;
  const {previousSibling} = textNode;
  const previousSiblingEndsWithLatinCharacter = endsWithLatinCharacter(previousSibling?.textContent ?? '');

  splitEmojisAndEmoticonsOutsideLinksAndChars(
    textNode.data,
    options.emojiValidationMap,
    options.shouldReplaceEmoticons,
    cursorPosition,
    previousSiblingEndsWithLatinCharacter,
  ).forEach(({text, emojiAndSkinTone}) => {
    // If we have nothing to replace, just increment the offset.
    if (!emojiAndSkinTone) {
      offset += text.length;
      return;
    }

    // Make sure we are where we think we are before mutating the text node.
    if (textNode.data.substring(offset, offset + text.length) !== text) {
      // eslint-disable-next-line no-console
      console.warn('Failed assertion while replacing emojis');
      return;
    }

    const node = createEmojiNode(document, emojiAndSkinTone, options);
    offset = spliceTextNode(textNode, offset, text.length, node);
    processEmojiNode(node, window);
  });
}

function createEmojiNode(
  document: Document,
  {emoji, skinTone}: EmojiDefinitionAndSkinTone,
  options: EmojiPluginOptions,
): HTMLImageElement {
  const img = document.createElement('img');
  img.setAttribute('data-front-emoji-shortcode', emojiToShortcode(emoji, skinTone));
  img.setAttribute('alt', renderEmojiText({emoji, skinTone}, options.renderMode));
  img.setAttribute('src', emojiToUrl(emoji, skinTone));

  // We will tune this in-context in a later processing step.
  // Note: we must use width/height attributes and NOT include 'px' for Outlook compatibility.
  const sizeAttrValue = initialSize.toFixed(0);
  img.setAttribute('width', sizeAttrValue);
  img.setAttribute('height', sizeAttrValue);
  img.style.verticalAlign = `${initialBottomMargin}px`;
  img.style.objectFit = 'contain';

  return img;
}

function renderEmojiText(
  {emoji, skinTone}: EmojiDefinitionAndSkinTone,
  renderMode: EmojiPluginRenderModesEnum,
): string {
  switch (renderMode) {
    case EmojiPluginRenderModesEnum.SHORTCODE:
      return emojiToShortcode(emoji, skinTone);

    case EmojiPluginRenderModesEnum.UNIFIED:
    default:
      return emojiToUnified(emoji, skinTone);
  }
}

function processEmojiNode(node: HTMLImageElement, window: FrontWindow): void {
  // This should return the computed font size in pixels, but check to make sure.
  const style = window.getComputedStyle(node);
  if (!style.fontSize || !style.fontSize.endsWith('px')) {
    return;
  }

  const fontSize = parseInt(style.fontSize, 10);
  const sizeAttrValue = Math.round(fontSize * sizeMultiplier).toFixed(0);
  const bottomMarginStyleValue = `${Math.round(fontSize * bottomMarginMultiplier * 1e5) / 1e5}px`;
  if (
    node.getAttribute('width') === sizeAttrValue &&
    node.getAttribute('height') === sizeAttrValue &&
    style.verticalAlign === bottomMarginStyleValue
  ) {
    return;
  }

  /* eslint-disable no-param-reassign */
  node.setAttribute('width', sizeAttrValue);
  node.setAttribute('height', sizeAttrValue);
  node.style.verticalAlign = bottomMarginStyleValue;
  /* eslint-enable no-param-reassign */
}

/*
 * Helpers.
 */

export function insertEmojiInEditor(editor: TextEditor, shortcode: string): void {
  const {textUnderCursor, edit, focus} = editor.services;

  // If there is a word character before the cursor, insert a space so that the
  // shortcode is replaced and to give the emoji room to breathe.
  const textBeforeCursor = getTextBeforeCursor(textUnderCursor.textUnderCursor);
  const prefix = endsWithLatinCharacter(textBeforeCursor) ? ' ' : '';

  edit.insertText(prefix + shortcode);
  focus.focus();
}
